import React, { useCallback, useEffect, useState } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ScreenHeader, SectionTitle, Card, useInputClass,
  PrimaryButton, SecondaryButton,
} from '../dojo';
import {
  watchStatus, generateToken, revokeToken, asDate, ConnectorStatus,
} from '../../services/connectorService';

/**
 * Connectors — one decision: connected, or not.
 *
 * taskDojo issues a token and Projectly holds it, reading and updating your
 * real tasks. Nothing is copied anywhere, so there is no sync to worry about
 * and nothing to go stale.
 *
 * There is deliberately no "test connection" button. taskDojo is the provider
 * here — it cannot call itself and learn anything, and a button that always
 * went green would be worse than none. What it shows instead is real: whether
 * Projectly has actually used the token, and when it last did.
 */
const Connectors: React.FC = () => {
  const { state } = useApp();
  const { isDark } = useTheme();
  const uid = state.user?.uid;
  const input = useInputClass();

  const colors = isDark
    ? { bg: 'bg-dojo-ink', text: 'text-neutral-100', sub: 'text-neutral-400', line: 'border-slate-700' }
    : { bg: 'bg-dojo-cream', text: 'text-black', sub: 'text-neutral-600', line: 'border-stone-300' };

  const [status, setStatus] = useState<ConnectorStatus | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [issued, setIssued] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!uid) return;
    return watchStatus(uid, (s) => { setStatus(s); setLoaded(true); });
  }, [uid]);

  const connected = !!status;
  const used = (status?.callCount || 0) > 0;
  const lastUsed = asDate(status?.lastUsedAt);

  const connect = useCallback(async () => {
    setBusy(true); setNotice(''); setCopied(false);
    try {
      setIssued(await generateToken('Projectly'));
    } catch (err: any) {
      setNotice(err?.message || "Couldn't create a token.");
    } finally {
      setBusy(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    setBusy(true); setNotice('');
    try {
      await revokeToken();
      setIssued(null);
      setNotice('Disconnected. Projectly can no longer see your tasks.');
    } catch (err: any) {
      setNotice(err?.message || "Couldn't disconnect.");
    } finally {
      setBusy(false);
    }
  }, []);

  const copy = () => {
    if (!issued) return;
    navigator.clipboard.writeText(issued).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className={`min-h-screen ${colors.bg} ${colors.text}`}>
      <div className="max-w-3xl mx-auto pt-4 pb-24">
        <ScreenHeader title="Connectors" subtitle="Let other tools work with your tasks" />

        {notice && (
          <div role="status" className="mx-5 mb-4 rounded-[14px] border-2 border-dojo-green/50
            bg-dojo-green/10 px-4 py-3 text-[13px] font-semibold text-dojo-green">
            {notice}
          </div>
        )}

        <section className="px-5 mb-6">
          <SectionTitle className="mb-3">Available</SectionTitle>
          <Card className="p-4">
            <>
            <div className="flex items-center gap-3">
              <span className="text-2xl leading-none">🔗</span>
              <h3 className="font-extrabold text-[15px] flex-1">Projectly</h3>
              <span className={`text-[11px] font-extrabold px-2 py-0.5 rounded-full ${
                used ? 'bg-dojo-green/15 text-dojo-green'
                  : connected ? 'bg-dojo-red/15 text-dojo-red'
                  : 'bg-neutral-500/15 ' + colors.sub
              }`}>
                {used ? '● Connected' : connected ? 'Awaiting Projectly' : 'Not connected'}
              </span>
            </div>

            <p className={`text-[13px] mt-2 mb-4 ${colors.sub}`}>
              Projectly reads and updates your tasks directly — status, progress,
              comments and the rest. Nothing is copied, so what it sees is always
              what is actually here. Only your own tasks are ever reachable.
            </p>

            {/* Shown once, and only once. The token cannot be retrieved later
                — not by you, not by us — so this is the moment it has to be
                copied across. */}
            {issued && (
              <div className="mb-4 rounded-[14px] border-2 border-dojo-green/50 bg-dojo-green/10 p-3">
                <p className="text-[13px] font-extrabold text-dojo-green mb-2">
                  🔑 Copy this now — it will not be shown again.
                </p>
                <div className="flex gap-2 items-center">
                  <code className={`flex-1 text-[11px] px-2 py-2 rounded-[10px] overflow-x-auto
                    whitespace-nowrap ${isDark ? 'bg-slate-800' : 'bg-stone-100'}`}>
                    {issued}
                  </code>
                  <PrimaryButton onClick={copy}>{copied ? 'Copied' : 'Copy'}</PrimaryButton>
                </div>
                <p className={`text-[12px] mt-2 ${colors.sub}`}>
                  In Projectly, go to Connectors → taskDojo and paste it there.
                </p>
              </div>
            )}

            {connected && !issued && (
              <div className={`mb-4 rounded-[12px] border-2 px-3 py-2.5 ${colors.line}`}>
                <p className={`text-[12.5px] font-semibold ${colors.sub}`}>
                  Token <code>{status?.tokenPrefix}…</code>
                  {used ? (
                    <> · {status?.callCount} request{status?.callCount === 1 ? '' : 's'} from Projectly
                      {lastUsed ? `, last ${lastUsed.toLocaleString('en-US',
                        { dateStyle: 'medium', timeStyle: 'short' })}` : ''}
                    </>
                  ) : (
                    <> · not used yet. Paste it into Projectly to finish connecting.</>
                  )}
                </p>
              </div>
            )}

            <div className={`flex flex-wrap gap-2 mt-5 pt-4 border-t-2 ${colors.line}`}>
              {!connected ? (
                <PrimaryButton onClick={connect} disabled={busy || !loaded}>
                  {busy ? 'Connecting…' : 'Connect'}
                </PrimaryButton>
              ) : (
                <>
                  <SecondaryButton onClick={connect} disabled={busy}>
                    {busy ? 'Working…' : 'Regenerate token'}
                  </SecondaryButton>
                  <SecondaryButton onClick={disconnect} disabled={busy}>Disconnect</SecondaryButton>
                </>
              )}
            </div>

            {connected && (
              <p className={`text-[12px] mt-3 ${colors.sub}`}>
                Regenerating replaces the old token immediately — useful if you think
                it has leaked. You will need to paste the new one into Projectly.
              </p>
            )}
            </>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default Connectors;
